/**
 * @license Copyright (c) 2003-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import ClassicEditorBase from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';

import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import UploadAdapter from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import CKFinder from '@ckeditor/ckeditor5-ckfinder/src/ckfinder';
import EasyImage from '@ckeditor/ckeditor5-easy-image/src/easyimage';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';
import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import Link from '@ckeditor/ckeditor5-link/src/link';
import List from '@ckeditor/ckeditor5-list/src/list';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import Table from '@ckeditor/ckeditor5-table/src/table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation';
import CloudServices from '@ckeditor/ckeditor5-cloud-services/src/cloudservices';
import PageBreak from '@ckeditor/ckeditor5-page-break/src/pagebreak';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import FontFamily from '@ckeditor/ckeditor5-font/src/fontfamily';
import FontColor from '@ckeditor/ckeditor5-font/src/fontcolor';
import FontBackgroundColor from '@ckeditor/ckeditor5-font/src/fontbackgroundcolor';
import FontSize from '@ckeditor/ckeditor5-font/src/fontsize';
import SourceEditing from '@ckeditor/ckeditor5-source-editing/src/sourceediting';
import RemoveFormat from '@ckeditor/ckeditor5-remove-format/src/removeformat';
import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline';
import Fullscreen from './Fullscreen';
export default class ClassicEditor extends ClassicEditorBase {}

// Plugins to include in the build.
ClassicEditor.builtinPlugins = [
	Essentials,
	UploadAdapter,
	Autoformat,
	Bold,
	Italic,
	BlockQuote,
	CKFinder,
	CloudServices,
	EasyImage,
	Heading,
	Image,
	ImageCaption,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	Indent,
	Link,
	List,
	MediaEmbed,
	Paragraph,
	PasteFromOffice,
	Table,
	TableToolbar,
	TextTransformation,
	PageBreak,
	Alignment,
	Strikethrough,
	Underline,
	FontFamily,
	FontSize,
	FontColor,
	FontBackgroundColor,
	SourceEditing,
	RemoveFormat,
	HorizontalLine,
	Fullscreen,
];

// Editor configuration.
ClassicEditor.defaultConfig = {
	toolbar: {
		items: [
			'heading',
			'|',
			'bold',
			'italic',
			'link',
			'bulletedList',
			'numberedList',
			'|',
			'outdent',
			'indent',
			'|',
			'uploadImage',
			'blockQuote',
			'insertTable',
			'mediaEmbed',
			'undo',
			'redo',
			'pageBreak',
			'alignment',
			'strikethrough',
			'underline',
			'fontFamily',
			'fontSize',
			'fontColor',
			'fontBackgroundColor',
			'sourceEditing',
			'removeFormat',
			'horizontalLine',
			'fullScreen',
		],
	},
	image: {
		toolbar: [
			'imageStyle:inline',
			'imageStyle:block',
			'imageStyle:side',
			'|',
			'toggleImageCaption',
			'imageTextAlternative',
		],
	},
	fontBackgroundColor: {
		colors: [
			{
				color: 'hsl(0, 0%, 0%)',
				label: 'Black',
			},
			{
				color: 'hsl(0, 0%, 30%)',
				label: 'Dim grey',
			},
			{
				color: 'hsl(0, 0%, 60%)',
				label: 'Grey',
			},
			{
				color: 'hsl(0, 0%, 90%)',
				label: 'Light grey',
			},
			{
				color: 'hsl(0, 0%, 100%)',
				label: 'White',
				hasBorder: true,
			},
			{
				color: 'hsl(0, 75%, 60%)',
				label: 'Red',
			},
			{
				color: 'hsl(30, 75%, 60%)',
				label: 'Orange',
			},
			{
				color: 'hsl(60, 75%, 60%)',
				label: 'Yellow',
			},
			{
				color: 'hsl(90, 75%, 60%)',
				label: 'Light green',
			},
			{
				color: 'hsl(120, 75%, 60%)',
				label: 'Green',
			},
			{
				color: 'hsl(150, 75%, 60%)',
				label: 'Aquamarine',
			},
			{
				color: 'hsl(180, 75%, 60%)',
				label: 'Turquoise',
			},
			{
				color: 'hsl(210, 75%, 60%)',
				label: 'Light blue',
			},
			{
				color: 'hsl(240, 75%, 60%)',
				label: 'Blue',
			},
			{
				color: 'hsl(270, 75%, 60%)',
				label: 'Purple',
			},
		],
	},
	table: {
		contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
	},
	fontSize: {
		options: [9, 11, 13, 'default', 17, 19, 21],
	},
	fontColorConfig: {
		colors: [
			{
				color: 'hsl(0, 0%, 0%)',
				label: 'Black',
			},
			{
				color: 'hsl(0, 0%, 30%)',
				label: 'Dim grey',
			},
			{
				color: 'hsl(0, 0%, 60%)',
				label: 'Grey',
			},
			{
				color: 'hsl(0, 0%, 90%)',
				label: 'Light grey',
			},
			{
				color: 'hsl(0, 0%, 100%)',
				label: 'White',
				hasBorder: true,
			},
			{
				color: 'hsl(0, 75%, 60%)',
				label: 'Red',
			},
			{
				color: 'hsl(30, 75%, 60%)',
				label: 'Orange',
			},
			{
				color: 'hsl(60, 75%, 60%)',
				label: 'Yellow',
			},
			{
				color: 'hsl(90, 75%, 60%)',
				label: 'Light green',
			},
			{
				color: 'hsl(120, 75%, 60%)',
				label: 'Green',
			},
			{
				color: 'hsl(150, 75%, 60%)',
				label: 'Aquamarine',
			},
			{
				color: 'hsl(180, 75%, 60%)',
				label: 'Turquoise',
			},
			{
				color: 'hsl(210, 75%, 60%)',
				label: 'Light blue',
			},
			{
				color: 'hsl(240, 75%, 60%)',
				label: 'Blue',
			},
			{
				color: 'hsl(270, 75%, 60%)',
				label: 'Purple',
			},
		],
	},

	// This value must be kept in sync with the language defined in webpack.config.js.
	language: 'en',
};
